import { getEnvConfig } from 'utils/getEnvConfig'
import {
  apiRequestInterceptor,
  apiResponseErrorInterceptor,
  apiResponseSuccessInterceptor,
  businessIdInterceptor
} from './apiInterceptor'
import ApiService from './ApiService'

const PURCHASED_SUBSCRIPTIONS_ENDPOINT = 'subscription/v2/GetPurchasedSubscriptions'
const LIST_PLANS_ENDPOINT = 'subscription/v2/ListOfferedSubscriptionPlans'
const PURCHASE_ENDPOINT = 'subscription/v2/PurchaseSubscription'
const PAYMENT_INTENT_ENDPOINT = 'payment/v1/CreatePaymentIntent'
const POST_PAYMENT_ENDPOINT = 'subscription/v1/PostPaymentId'
const PAYMENT_STATUS_ENDPOINT = 'payment/v1/payment'
const MOBILE_SUBSCRIPTIONS_ENDPOINT = 'subscription/v1/GetMobileSubscriptions'

const headers = {
  'content-type': 'application/json',
  accept: 'application/json, text/plain, */*'
}

const endpoint = getEnvConfig('WEB_BASE_URL') || ''

const api = new ApiService(endpoint, headers)

api.requestInterceptor(apiRequestInterceptor, (error: any) => console.error(error))
api.requestInterceptor(businessIdInterceptor, (error: any) => console.error(error))
api.responseInterceptor(apiResponseSuccessInterceptor, apiResponseErrorInterceptor)

export type benefits =
  | 'UNLIMITED_TXNS'
  | 'UNLIMITED_TXN_SMS_FROM_OKC'
  | 'AD_FREE'
  | 'MULTI_DEVICE'
  | 'DESKTOP_ACCESS'
  | 'LIFETIME_BILL_ACCESS'
  | 'GST_BILL'
  | 'VIEW_DEFAULTER'
  | 'PRIORITY_CUSTOMER_SUPPORT'

export const BENEFIT_DESKTOP_ACCESS: benefits = 'DESKTOP_ACCESS'
export interface SubscriptionPlan {
  id: string
  name: string
  amount: number
  discount: number
  benefits: benefits[]
  validity_in_days: number
}

export interface ListPlansResponse {
  subscription_plans: SubscriptionPlan[]
}

export interface PurchasedSubscription {
  plan_id: string
  name: string
  amount: number
  start_time: number
  expiry_time: number
  benefits: benefits[]
}

export interface PurchasedSubscriptionsResponse {
  last_expired_sub: PurchasedSubscription | null
  active_sub: PurchasedSubscription | null
  future_subs: PurchasedSubscription[]
  active_mandate: any
}

export interface PaymentDetails {
  payment_link: string
  link_id: string
  subscription_payment_id: string
}

export interface PurchaseSubscriptionResponse {
  type: number
  added_subscription: PurchasedSubscription | null
  payment_detail: PaymentDetails | null
}

export interface PaymentIntent {
  type: string
  url: string
}

export interface CreatePaymentIntentResponse {
  amount: number
  order_id: string
  order_token: string | null
  payment_id: string
  payment_intent: PaymentIntent | null
  provider: string | null
  provider_account_id: string | null
  provider_key_id: string | null
}

export interface PaymentInfo {
  id: string
  link_id: string
  create_time: number
  update_time: number
  payment_amount: string
  payout_amount: string
  refund_amount: string
}

export interface PaymentStatusResponse {
  status: string
  payment_id: string
  payment: PaymentInfo
}
export const getPurchasedSubscriptions = (): Promise<{ data: PurchasedSubscriptionsResponse }> => {
  return api.getRequest(PURCHASED_SUBSCRIPTIONS_ENDPOINT, {})
}

export const listOfferedSubscriptionPlans = (): Promise<{ data: ListPlansResponse }> => {
  return api.getRequest(LIST_PLANS_ENDPOINT, {})
}

export const purchaseSubscription = (
  planId: string,
  labels: Record<string, string> = {}
): Promise<{ data: PurchaseSubscriptionResponse }> => {
  return api.postRequest(PURCHASE_ENDPOINT, {
    plan_id: planId,
    labels
  })
}

export const createPaymentIntent = (
  amount: number,
  linkId: string,
  paymentMethodType: string = 'upi',
  referenceId: string | null = null
): Promise<{ data: CreatePaymentIntentResponse }> => {
  return api.postRequest(PAYMENT_INTENT_ENDPOINT, {
    amount,
    link_id: linkId,
    payment_method_type: paymentMethodType,
    reference_id: referenceId
  })
}

export const postPaymentId = (paymentId: string, subscriptionPaymentId: string): Promise<void> => {
  return api.postRequest(POST_PAYMENT_ENDPOINT, {
    payment_id: paymentId,
    subscription_payment_id: subscriptionPaymentId
  })
}

export const getPaymentStatus = (paymentId: string): Promise<{ data: PaymentStatusResponse }> => {
  return api.getRequest(`${PAYMENT_STATUS_ENDPOINT}/${paymentId}`, { polling: true })
}

// Which of these mobiles hold an active Premium plan (Android
// OkSubscriptionRepository.getMobileSubscriptions): mobile → plan name, null
// when none. Failures answer an empty map so avatars simply show no badge.
export const getMobileSubscriptions = async (
  mobiles: string[]
): Promise<Record<string, string | null>> => {
  if (mobiles.length === 0) {
    return {}
  }
  try {
    const response: { data?: { subscriptions?: Record<string, string | null> } } =
      await api.postRequest(MOBILE_SUBSCRIPTIONS_ENDPOINT, { mobile_numbers: mobiles })
    return response?.data?.subscriptions || {}
  } catch (error) {
    console.warn('GetMobileSubscriptions failed', error)
    return {}
  }
}

export const SubscriptionApi = {
  getPurchasedSubscriptions,
  listOfferedSubscriptionPlans,
  purchaseSubscription,
  createPaymentIntent,
  postPaymentId,
  getPaymentStatus
}
